
import {readOptOut} from 'utils/privacy';

import {EventBatch, SessionContext, TrackedEvent} from './types';

const FLUSH_MS = 5000;
const FLUSH_AT = 50;
// Server caps a batch at 500 events / 1MB
const MAX_BATCH = 200;
// Memory bound while the endpoint is unreachable
const MAX_QUEUE = 1000;
// sendBeacon payload limit is ~64KB across the page
const BEACON_MAX_BYTES = 60000;
const MAX_BACKOFF_MS = 60000;

export const buildBatch = (
	key: string, session: SessionContext, events: TrackedEvent[],
): EventBatch => ({
	k: key,
	sent_at: Date.now(),
	session,
	events,
});

// Each event keeps the session context it was queued under; a batch only
// ever holds events that share one session + user
type Entry = {event: TrackedEvent, context: SessionContext};

const contextKey = (c: SessionContext) => `${c.id}|${c.anon_id}|${c.user_id || ''}`;

// Leading run of entries that can share one batch (same context key)
const takeRun = (queue: Entry[], max: number): number => {
	if (!queue.length) {
		return 0;
	}
	const key = contextKey(queue[0].context);
	let n = 1;
	while (n < queue.length && n < max && contextKey(queue[n].context) === key) {
		n++;
	}
	return n;
};

export class Transport {
	private queue: Entry[] = [];
	private timer: ReturnType<typeof setTimeout> | null = null;
	private backoffMs = 0;
	private inflight = false;

	constructor(
		private endpoint: string,
		private key: string,
	) {}

	enqueue(event: TrackedEvent, context: SessionContext) {
		this.queue.push({event, context});
		if (this.queue.length > MAX_QUEUE) {
			this.queue.splice(0, this.queue.length - MAX_QUEUE);
		}
		if (this.queue.length >= FLUSH_AT) {
			void this.flush();
		} else {
			this.schedule(FLUSH_MS);
		}
	}

	clear() {
		this.queue = [];
		if (this.timer) {
			clearTimeout(this.timer);
			this.timer = null;
		}
	}

	private schedule(ms: number) {
		if (!this.timer) {
			this.timer = setTimeout(() => {
				this.timer = null;
				void this.flush();
			}, ms);
		}
	}

	async flush(): Promise<void> {
		// Opt-out can arrive mid-session (account sync); drop everything
		if (readOptOut()) {
			return this.clear();
		}
		if (this.inflight || !this.queue.length) {
			return;
		}
		const entries = this.queue.splice(0, takeRun(this.queue, MAX_BATCH));
		const batch = buildBatch(this.key, entries[0].context, entries.map((e) => e.event));
		this.inflight = true;
		try {
			const r = await fetch(`${this.endpoint}/e`, {
				method: 'POST',
				// text/plain keeps this a "simple" request: no CORS preflight
				headers: {'Content-Type': 'text/plain'},
				// No keepalive: it caps bodies at 64KB; flushOnHide covers unload
				body: JSON.stringify(batch),
			});
			if (r.status === 429 || r.status >= 500) {
				throw new Error(`retryable ${r.status}`);
			}
			// Other 4xx: the batch itself is bad; retrying won't help
			this.backoffMs = 0;
		} catch {
			this.queue.unshift(...entries);
			this.backoffMs = Math.min(MAX_BACKOFF_MS, (this.backoffMs || 1000) * 2);
			this.schedule(this.backoffMs);
			return;
		} finally {
			this.inflight = false;
		}
		if (this.queue.length) {
			this.schedule(0);
		}
	}

	// Page is going away: hand whatever fits to the browser via sendBeacon
	flushOnHide() {
		if (readOptOut() || !this.queue.length || !navigator.sendBeacon) {
			return;
		}
		const encode = (n: number) => JSON.stringify(buildBatch(
			this.key, this.queue[0].context, this.queue.slice(0, n).map((e) => e.event),
		));
		while (this.queue.length) {
			let n = takeRun(this.queue, MAX_BATCH);
			let body = encode(n);
			while (body.length > BEACON_MAX_BYTES && n > 1) {
				n = Math.floor(n / 2);
				body = encode(n);
			}
			const blob = new Blob([body], {type: 'text/plain'});
			if (!navigator.sendBeacon(`${this.endpoint}/e`, blob)) {
				return;
			}
			this.queue.splice(0, n);
		}
	}
}
